/*
 * Copyright (c) 2017, WSO2 Inc. (http://www.wso2.org) All Rights Reserved.
 *
 * WSO2 Inc. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
"use strict";
import APIClientBdv from "./APIClientBdv";
import Utils from "./Utils";
import axios from 'axios';
import AuthManager from './AuthManager';
import Cookies from 'js-cookie';
import RequestCryptoService from './RequestCryptoService';
import { Mutex } from 'async-mutex';

const getHeaderValue = (headers, key) => {
    if (!headers) {
        return undefined;
    }

    if (headers[key]) {
        return headers[key];
    }

    const normalizedKey = Object.keys(headers).find((headerKey) => {
        return headerKey && headerKey.toLowerCase() === key.toLowerCase();
    });

    return normalizedKey ? headers[normalizedKey] : undefined;
};

const encryptRequestBodyIfNeeded = (config) => {
    if (!config || !config.method) {
        return config;
    }

    const method = String(config.method).toLowerCase();
    const hasBody = config.data !== undefined && config.data !== null;
    if ((method === 'post' || method === 'put') && hasBody) {
        config.data = {
            key: RequestCryptoService.encrypt(null, config.data),
        };
    }

    return config;
};

const ensureTokenClaimsContext = () => {
    const existingContext = RequestCryptoService.getTokenClaimsContext();
    if (existingContext && existingContext.sub && existingContext.exp) {
        return Promise.resolve(existingContext);
    }

    return AuthManager.getUserFromToken()
        .then(() => RequestCryptoService.getTokenClaimsContext())
        .catch(() => null);
};

const decryptResponseBodyIfNeeded = (response) => {
    const encrypted = response?.data?.responseBody?.key ?? response?.data?.key;
    if (!encrypted) {
        return response;
    }
    const decrypted = RequestCryptoService.decrypt(null, encrypted);

    if (response?.data?.responseBody?.key) {
        response.data.responseBody = decrypted;
    } else {
        response.data = decrypted;
    }

    return response;
};

class APIClientFactoryBdv {
    constructor() {
        this.environment = Utils.getCurrentEnvironment();
        if (APIClientFactoryBdv._instance) {
            return APIClientFactoryBdv._instance;
        }

        this._APIClientMap = new Map();
        this.mutex = new Mutex();
        APIClientFactoryBdv._instance = this;
    }

    getAPIClient() {
        const client = axios.create({
            //baseURL: 'https://localhost:9000/apis/bdv/',
            baseURL: '/am/devportal/v3/apis/bdv/',
            withCredentials: true,
        });

        client.interceptors.request.use(
            (config) => {
                const cookie = Cookies.get('AM_ACC_TOKEN_DEFAULT_P2');
                config.headers['Content-Type'] = 'application/json';

                const finalizeWithEncryption = () => {
                    return ensureTokenClaimsContext().then(() => encryptRequestBodyIfNeeded(config));
                };

                const existingUser = AuthManager.getUser();

                if (!existingUser) {
                    return finalizeWithEncryption();
                }

                let existingToken = AuthManager.getUser().getPartialToken();
                const refToken = AuthManager.getUser().getRefreshPartialToken();

                if (existingToken) {
                    config.headers['Authorization'] = 'Bearer ' + existingToken;
                    return finalizeWithEncryption();
                } else {
                    if (!refToken) {
                        return finalizeWithEncryption();
                    }
                }

                const env = this.environment;
                const promise = this.mutex.acquire().then((release) => {
                    existingToken = AuthManager.getUser(env.label).getPartialToken();

                    if (existingToken) {
                        config.headers['Authorization'] = 'Bearer ' + existingToken;
                        release();
                        return finalizeWithEncryption();
                    } else {
                        return AuthManager.refresh(env).then((res) => res.json())
                            .then(() => {
                                config.headers['Authorization'] = 'Bearer '
                                    + AuthManager.getUser(env.label).getPartialToken();
                                return finalizeWithEncryption();
                            }).catch((error) => {
                            })
                            .finally(() => {
                                release();
                            });
                    }
                });

                return promise;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Add a response interceptor
        client.interceptors.response.use(
            async (response) => {
                await ensureTokenClaimsContext();
                decryptResponseBodyIfNeeded(response);

                if (response.headers.etag) {
                    APIClientBdv.addETag(response.config.url, response.headers.etag);
                }

                if (response.status === 401) {
                    const userData = AuthManager.getUserFromToken();
                    const existingUser = AuthManager.getUser();
                    if (existingUser) {
                        userData.then((user) => {
                            if (user) {
                                window.location = Settings.app.context + Utils.CONST.LOGOUT_CALLBACK;
                            }
                        }).catch((error) => {
                            console.error('Error occurred while checking token status. Hence redirecting to login', error);
                            window.location = Settings.app.context + Utils.CONST.LOGOUT_CALLBACK;
                        });
                    } else {
                        console.error('Attempted a call to a protected API without a proper access token');
                    }
                }
                return response;
            },
            async (error) => {
                const response = error.response;
                await ensureTokenClaimsContext();
                decryptResponseBodyIfNeeded(response);

                if (response && response.status === 401) {
                    const userData = AuthManager.getUserFromToken();
                    const existingUser = AuthManager.getUser();
                    if (existingUser) {
                        userData.then((user) => {
                            if (user) {
                                window.location = Settings.app.context + Utils.CONST.LOGOUT_CALLBACK;
                            }
                        }).catch((error) => {
                            console.error('Error occurred while checking token status. Hence redirecting to login', error);
                            window.location = Settings.app.context + Utils.CONST.LOGOUT_CALLBACK;
                        });
                    } else {
                        console.error('Attempted a call to a protected API without a proper access token');
                    }
                }
                return Promise.reject(error);
            }
        );

        return client;
    }
}

APIClientFactoryBdv._instance = null;

export default APIClientFactoryBdv;